import { useState, createContext, useContext, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';

// Context for global confirmation dialogs
const ConfirmationContext = createContext(null);

// Dialog types with their icons and colors
const DIALOG_TYPES = {
  confirmation: {
    icon: CheckCircle,
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-600',
    defaultTitle: 'Confirmation'
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    titleColor: 'text-amber-600',
    defaultTitle: 'Warning'
  },
  error: {
    icon: XCircle,
    iconColor: 'text-red-500',
    titleColor: 'text-red-600',
    defaultTitle: 'Error'
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-600',
    defaultTitle: 'Information'
  },
  delete: {
    icon: AlertTriangle,
    iconColor: 'text-red-500',
    titleColor: 'text-red-600',
    defaultTitle: 'Delete Confirmation'
  }
};

export function ConfirmationProvider({ children }) {
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    message: '',
    type: 'confirmation',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null
  });

  const confirm = useCallback(({
    title,
    message,
    type = 'confirmation',
    confirmText = 'Confirm',
    cancelText = 'Cancel'
  }) => {
    return new Promise((resolve) => {
      setDialogState({
        open: true,
        title: title || DIALOG_TYPES[type]?.defaultTitle || 'Confirmation',
        message,
        type,
        confirmText,
        cancelText,
        onConfirm: () => {
          setDialogState(prev => ({ ...prev, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialogState(prev => ({ ...prev, open: false }));
          resolve(false);
        }
      });
    });
  }, []);

  const typeConfig = DIALOG_TYPES[dialogState.type] || DIALOG_TYPES.confirmation;
  const IconComponent = typeConfig.icon;

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={dialogState.open} onOpenChange={(open) => {
        if (!open) dialogState.onCancel?.();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${typeConfig.titleColor}`}>
              <IconComponent className={`h-5 w-5 ${typeConfig.iconColor}`} />
              {dialogState.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              {dialogState.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={dialogState.onCancel}>
              {dialogState.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={dialogState.onConfirm}
              className={dialogState.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {dialogState.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationContext.Provider>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
}

// Simple standalone confirm function (fallback for components not in provider)
export const showConfirm = async ({ title, message, type = 'confirmation' }) => {
  // This is a fallback that uses native confirm, but with better messaging
  return window.confirm(`${title}\n\n${message}`);
};
